import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, Subscription, distinctUntilChanged, map } from 'rxjs';

import * as PseudoLocalActions from '@fcom/core/actions/pseudo-local.actions';
import { AppState } from '@fcom/core';
import { pseudoLocal } from '@fcom/core/selectors/pseudo-local.selector';
import { finShare, snapshot } from '@fcom/rx';
import { unsubscribe } from '@fcom/core/utils/unsubscribe';

import { TARGET_HEIGHT_ELEMENT } from './expand-content.component';

@Component({
  selector: 'fin-expand',
  templateUrl: 'expand.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ExpandComponent implements OnInit, OnDestroy {
  private store$ = inject<Store<AppState>>(Store);

  @Input()
  id: string;

  @Input()
  key: string;

  @Input()
  initiallyOpen: boolean;

  @Input()
  clearSelectionOnDestroy = false;

  @Input()
  expandContainerClass?: string | string[];

  @Input()
  expandContentClass?: string | string[];

  @Input()
  disabled = false;

  @Input()
  targetHeightElement: TARGET_HEIGHT_ELEMENT = TARGET_HEIGHT_ELEMENT.CHILDREN;

  @Input()
  useCustomTrigger = false;

  @Output()
  expandOpen: EventEmitter<boolean> = new EventEmitter<boolean>();

  subscription: Subscription = new Subscription();
  isOpen$: Observable<boolean>;
  initialValue: boolean;

  ngOnInit(): void {
    if (this.initiallyOpen) {
      this.store$.dispatch(PseudoLocalActions.setValue({ key: this.key, data: this.id }));
    }

    // The get initial render not to flicker when open
    this.initialValue = snapshot(this.store$.pipe(pseudoLocal(this.key))) === this.id;

    this.expandOpen.emit(this.initialValue);

    const openState$: Observable<boolean> = this.store$.pipe(
      pseudoLocal(this.key),
      map((value) => value === this.id),
      distinctUntilChanged(),
      finShare()
    );

    this.subscription.add(openState$.subscribe((item) => this.expandOpen.emit(item)));
    this.isOpen$ = openState$;
  }

  ngOnDestroy(): void {
    if (this.clearSelectionOnDestroy) {
      this.store$.dispatch(PseudoLocalActions.setValue({ key: this.key, data: undefined }));
    }
    this.subscription = unsubscribe(this.subscription);
  }

  toggleDetails(event?: Event): void {
    const currentSnapshot = snapshot(this.store$.pipe(pseudoLocal(this.key)));
    const currentValue: string | undefined = typeof currentSnapshot === 'string' ? currentSnapshot : undefined;
    const newValue: string = currentValue === this.id ? undefined : this.id;
    this.store$.dispatch(PseudoLocalActions.setValue({ key: this.key, data: newValue }));
    event?.preventDefault();
  }
}
